/**
 * Overview — analytics dashboard.
 * Ticket 3.6: real data from GET /analytics/summary + GET /analytics/activity.
 * Design source: pages/overview.jsx (claude_design project 5c0836b6-…).
 */

import React, { useMemo } from 'react';
import {
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsActivityQuery,
  BreakdownRow,
  FunnelRow,
  ActivityItem,
  EngagementMetrics,
  SummaryCards,
} from 'src/store/analyticsApi';
import { PageHead } from 'src/ui/patterns/PageHead';
import { PoolHealth } from './poolHealth';
import { LoadingBlock } from 'src/ui/Spinner';
import { Alert } from 'src/ui/Alert';
import { Icon } from 'src/ui/Icon';
import type { IconName } from 'src/ui/Icon';

// ---- helpers ---------------------------------------------------------------

const nf = (n: number | null | undefined): string =>
  n == null ? '—' : Number(n).toLocaleString('en-US');

const pctFmt = (n: number | null | undefined, decimals = 1): string =>
  n == null ? 'N/A' : n.toFixed(decimals) + '%';

function solid(color: string): string {
  return color === 'accent' ? 'var(--accent)' : `var(--${color}-solid)`;
}
function softBg(color: string): string {
  return color === 'accent' ? 'var(--accent-subtle)' : `var(--${color}-bg)`;
}
function textC(color: string): string {
  return color === 'accent' ? 'var(--accent-text)' : `var(--${color}-text)`;
}

// ---- Section card ----------------------------------------------------------

interface SectionCardProps {
  title: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  pad?: number;
}

function SectionCard({
  title,
  right,
  children,
  style,
  pad = 18,
}: SectionCardProps) {
  return (
    <div
      style={{
        background: 'var(--bg-canvas)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '13px 16px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span className="panel-title" style={{ whiteSpace: 'nowrap' }}>
          {title}
        </span>
        {right}
      </div>
      <div style={{ padding: pad, flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// ---- KPI card --------------------------------------------------------------

interface KpiDef {
  key: keyof SummaryCards;
  label: string;
  icon: IconName;
  color: string;
  fmt: 'number' | 'pct';
  sub?: string;
}

const KPI_DEFS: KpiDef[] = [
  {
    key: 'activeToday',
    label: 'Active today',
    icon: 'activity',
    color: 'accent',
    fmt: 'number',
    sub: 'since midnight',
  },
  {
    key: 'weeklyActive',
    label: 'Weekly active',
    icon: 'users',
    color: 'blue',
    fmt: 'number',
    sub: 'last 7 days',
  },
  {
    key: 'newSignups7d',
    label: 'New signups',
    icon: 'sparkles',
    color: 'green',
    fmt: 'number',
    sub: 'last 7 days',
  },
  {
    key: 'premiumConversionPct',
    label: 'Premium rate',
    icon: 'zap',
    color: 'purple',
    fmt: 'pct',
    sub: 'premium + trial',
  },
];

interface KpiCardProps {
  def: KpiDef;
  value: number;
}

function KpiCard({ def, value }: KpiCardProps) {
  const display = def.fmt === 'pct' ? value.toFixed(1) + '%' : nf(value);

  return (
    <div
      style={{
        background: 'var(--bg-canvas)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        padding: 16,
      }}
    >
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <div className="row gap-2" style={{ color: 'var(--text-muted)' }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-md)',
              background: softBg(def.color),
              color: textC(def.color),
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={def.icon} size={15} />
          </span>
          <span
            style={{
              fontSize: 'var(--fs-xs)',
              fontWeight: 500,
              color: 'var(--text-secondary)',
            }}
          >
            {def.label}
          </span>
        </div>
      </div>
      <div
        className="row"
        style={{
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginTop: 10,
          gap: 8,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 30,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1,
            }}
          >
            {display}
          </div>
          {def.sub && (
            <div
              className="text-faint"
              style={{
                fontSize: 'var(--fs-2xs)',
                marginTop: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
              }}
            >
              {def.sub}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Onboarding funnel -----------------------------------------------------

function Funnel({ rows }: { rows: FunnelRow[] }) {
  const top = rows[0]?.count ?? 0;
  return (
    <div className="col" style={{ gap: 9 }}>
      {rows.map((f) => {
        const pct = top > 0 ? (f.count / top) * 100 : 0;
        return (
          <div key={f.step}>
            <div
              className="row"
              style={{
                justifyContent: 'space-between',
                gap: 10,
                marginBottom: 5,
              }}
            >
              <span
                className="truncate"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                }}
              >
                {f.label}
              </span>
              <span
                className="row gap-2"
                style={{ flexShrink: 0, fontSize: 'var(--fs-xs)' }}
              >
                <span className="mono" style={{ fontWeight: 600 }}>
                  {nf(f.count)}
                </span>
                <span
                  className="text-faint"
                  style={{
                    fontSize: 'var(--fs-2xs)',
                    width: 34,
                    textAlign: 'right',
                  }}
                >
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 99,
                background: 'var(--bg-muted)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: pct + '%',
                  borderRadius: 99,
                  background: 'var(--accent)',
                  opacity: 0.45 + 0.55 * (f.pct / 100),
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Donut (membership) ----------------------------------------------------

interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

function Donut({
  segments,
  total,
}: {
  segments: DonutSegment[];
  total: number;
}) {
  const R = 54;
  const C = 2 * Math.PI * R;
  const sw = 20;

  let off = 0;
  const arcs = segments.map((s, i) => {
    const len = total > 0 ? (s.value / total) * C : 0;
    const el = (
      <circle
        key={i}
        cx={66}
        cy={66}
        r={R}
        fill="none"
        stroke={solid(s.color)}
        strokeWidth={sw}
        strokeDasharray={`${len.toFixed(2)} ${(C - len).toFixed(2)}`}
        strokeDashoffset={(-off).toFixed(2)}
      />
    );
    off += len;
    return el;
  });

  return (
    <div className="row" style={{ gap: 20, alignItems: 'center' }}>
      <div
        style={{
          position: 'relative',
          width: 132,
          height: 132,
          flexShrink: 0,
        }}
      >
        <svg viewBox="0 0 132 132" width={132} height={132}>
          <g transform="rotate(-90 66 66)">
            <circle
              cx={66}
              cy={66}
              r={R}
              fill="none"
              stroke="var(--bg-muted)"
              strokeWidth={sw}
            />
            {arcs}
          </g>
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {nf(total)}
          </div>
          <div
            className="text-faint"
            style={{
              fontSize: 'var(--fs-2xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 600,
            }}
          >
            members
          </div>
        </div>
      </div>
      <div className="col" style={{ gap: 10, flex: 1 }}>
        {segments.map((s) => (
          <div
            key={s.label}
            className="row"
            style={{ justifyContent: 'space-between' }}
          >
            <span className="row gap-2" style={{ fontSize: 'var(--fs-sm)' }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 3,
                  background: solid(s.color),
                }}
              />
              {s.label}
            </span>
            <span className="row gap-2">
              <span
                className="mono"
                style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}
              >
                {nf(s.value)}
              </span>
              <span
                className="text-faint"
                style={{
                  fontSize: 'var(--fs-xs)',
                  width: 34,
                  textAlign: 'right',
                }}
              >
                {total > 0 ? ((s.value / total) * 100).toFixed(0) : 0}%
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Status bars (account status) ------------------------------------------

interface StatusBarItem {
  label: string;
  value: number;
  color: string;
}

function StatusBars({ items }: { items: StatusBarItem[] }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="col" style={{ gap: 13 }}>
      {items.map((it) => (
        <div key={it.label}>
          <div
            className="row"
            style={{ justifyContent: 'space-between', marginBottom: 5 }}
          >
            <span className="row gap-2" style={{ fontSize: 'var(--fs-sm)' }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 3,
                  background: solid(it.color),
                }}
              />
              {it.label}
            </span>
            <span
              className="mono"
              style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}
            >
              {nf(it.value)}
            </span>
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 99,
              background: 'var(--bg-muted)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: ((it.value / max) * 100).toFixed(1) + '%',
                borderRadius: 99,
                background: solid(it.color),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Engagement metrics ----------------------------------------------------

function EngagementCard({ data }: { data: EngagementMetrics }) {
  const rows: { label: string; hint: string; value: string }[] = [
    {
      label: 'Match rate',
      hint: 'mutual likes / total likes sent',
      value: pctFmt(data.matchRatePct),
    },
    {
      label: 'Avg messages / chat',
      hint: 'active chats, last 7 days',
      value:
        data.avgMessagesPerChat7d != null
          ? data.avgMessagesPerChat7d.toFixed(1)
          : '—',
    },
    {
      label: 'Reply rate',
      hint: 'chats with ≥ 2 senders',
      value: pctFmt(data.replyRatePct),
    },
    {
      label: 'D7 retention',
      hint: 'not yet tracked',
      value: data.d7RetentionPct != null ? pctFmt(data.d7RetentionPct) : 'N/A',
    },
  ];

  return (
    <div className="col" style={{ gap: 0 }}>
      {rows.map((s, i) => (
        <div
          key={s.label}
          className="row"
          style={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: '11px 0',
            borderBottom:
              i < rows.length - 1 ? '1px solid var(--border)' : 'none',
          }}
        >
          <div>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
              {s.label}
            </div>
            <div
              className="text-faint"
              style={{ fontSize: 'var(--fs-2xs)', marginTop: 1 }}
            >
              {s.hint}
            </div>
          </div>
          <div
            className="mono"
            style={{
              fontSize: 'var(--fs-lg)',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Activity feed ---------------------------------------------------------

const ACT_CONFIG: Record<string, { icon: IconName; color: string }> = {
  match: { icon: 'heart', color: 'pink' },
  signup: { icon: 'sparkles', color: 'green' },
  upgrade: { icon: 'zap', color: 'purple' },
  rsvp: { icon: 'calendar', color: 'blue' },
  message_burst: { icon: 'message-circle', color: 'gray' },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function ActivityFeed({ items }: { items: ActivityItem[] }) {
  if (!items.length) {
    return (
      <div
        className="text-muted"
        style={{ fontSize: 'var(--fs-sm)', padding: '12px 0' }}
      >
        No recent activity.
      </div>
    );
  }

  return (
    <div className="col" style={{ gap: 2 }}>
      {items.map((a, i) => {
        const cfg = ACT_CONFIG[a.type] ?? ACT_CONFIG.message_burst;
        return (
          <div
            key={i}
            className="row gap-3"
            style={{
              padding: '9px 0',
              borderBottom:
                i < items.length - 1 ? '1px solid var(--border)' : 'none',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 99,
                background: softBg(cfg.color),
                color: textC(cfg.color),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={cfg.icon} size={13} />
            </span>
            <span
              className="grow"
              style={{ fontSize: 'var(--fs-sm)', minWidth: 0 }}
            >
              {a.title}
            </span>
            <span
              className="text-faint mono"
              style={{ fontSize: 'var(--fs-2xs)', flexShrink: 0 }}
            >
              {relativeTime(a.timestamp)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---- Color maps for breakdown rows ----------------------------------------

const MEMBERSHIP_COLORS: Record<string, string> = {
  Freemium: 'gray',
  'Premium Trial': 'blue',
  Premium: 'purple',
};

const ACCOUNT_STATUS_COLORS: Record<string, string> = {
  Active: 'green',
  Paused: 'amber',
  'Skip Match': 'blue',
  Blocked: 'red',
};

// ---- Page ------------------------------------------------------------------

const Overview: React.FC = () => {
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useGetAnalyticsSummaryQuery();

  const {
    data: activity,
    isLoading: activityLoading,
    isError: activityError,
  } = useGetAnalyticsActivityQuery({ limit: 20 });

  // Derived: map BreakdownRow[] → DonutSegment[] / StatusBarItem[]
  const membershipSegments = useMemo(() => {
    if (!summary) return [];
    return summary.membership.map((r: BreakdownRow) => ({
      label: r.label,
      value: r.count,
      color: MEMBERSHIP_COLORS[r.label] ?? 'gray',
    }));
  }, [summary]);

  const accountStatusItems = useMemo(() => {
    if (!summary) return [];
    return summary.accountStatus.map((r: BreakdownRow) => ({
      label: r.label,
      value: r.count,
      color: ACCOUNT_STATUS_COLORS[r.label] ?? 'gray',
    }));
  }, [summary]);

  const funnelRows: FunnelRow[] = summary?.onboardingFunnel ?? [];
  const completedPct =
    funnelRows.length > 0
      ? funnelRows[funnelRows.length - 1].pct.toFixed(0) + '% complete'
      : '';

  const generatedAt = summary?.generatedAt
    ? new Date(summary.generatedAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const liveDot = (
    <span
      className="row gap-2"
      style={{
        fontSize: 'var(--fs-2xs)',
        color: 'var(--green-text)',
        fontWeight: 600,
      }}
    >
      <span className="live-dot" />
      Live
    </span>
  );

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <PageHead
        title="Overview"
        subtitle={
          generatedAt
            ? `App usage at a glance · updated ${generatedAt}`
            : 'App usage at a glance'
        }
      />

      <div className="grow scroll-y" style={{ padding: 22 }}>
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Loading / error states for summary */}
          {summaryLoading && <LoadingBlock label="Loading analytics…" />}
          {summaryError && (
            <Alert color="red" title="Failed to load analytics summary">
              Please check your connection and try reloading.
            </Alert>
          )}

          {summary && (
            <>
              {/* KPI row */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 16,
                }}
              >
                {KPI_DEFS.map((def) => (
                  <KpiCard
                    key={def.key}
                    def={def}
                    value={summary.cards[def.key]}
                  />
                ))}
              </div>

              {/* Onboarding funnel */}
              <SectionCard
                title="Onboarding funnel"
                right={
                  <span
                    className="text-faint"
                    style={{ fontSize: 'var(--fs-2xs)' }}
                  >
                    {completedPct}
                  </span>
                }
              >
                <Funnel rows={funnelRows} />
              </SectionCard>

              {/* Membership + account status + engagement */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 16,
                }}
              >
                <SectionCard title="Membership">
                  <Donut
                    segments={membershipSegments}
                    total={summary.membershipTotal}
                  />
                </SectionCard>
                <SectionCard title="Account status">
                  <StatusBars items={accountStatusItems} />
                </SectionCard>
                <SectionCard title="Engagement">
                  <EngagementCard data={summary.engagement} />
                </SectionCard>
              </div>
            </>
          )}

          {/* Candidate pool distribution (supply side) — admin-generated snapshot */}
          <PoolHealth />

          {/* Activity feed — loads independently */}
          <SectionCard title="Live activity" right={liveDot}>
            {activityLoading && <LoadingBlock label="Loading activity…" />}
            {activityError && (
              <Alert color="red" title="Failed to load activity feed">
                Please try reloading.
              </Alert>
            )}
            {activity && <ActivityFeed items={activity.items} />}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

// ---- Win-back inactive users ----------------------------------------------

export default Overview;
